/**
 * 🌈 Mood Logger Component
 * Allows users to log their mood anytime from the Health mode
 */

import React, { useState, useEffect } from 'react';
import { X, Smile, Frown, Meh, AlertCircle, Zap, Moon, Heart, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const MOOD_OPTIONS = [
  { value: 'great', emoji: '😊', label: 'Great', color: 'bg-green-100 hover:bg-green-200 border-green-300' },
  { value: 'good', emoji: '🙂', label: 'Good', color: 'bg-teal-100 hover:bg-teal-200 border-teal-300' },
  { value: 'okay', emoji: '😐', label: 'Okay', color: 'bg-yellow-100 hover:bg-yellow-200 border-yellow-300' },
  { value: 'sad', emoji: '😢', label: 'Sad', color: 'bg-blue-100 hover:bg-blue-200 border-blue-300' },
  { value: 'anxious', emoji: '😰', label: 'Anxious', color: 'bg-purple-100 hover:bg-purple-200 border-purple-300' },
  { value: 'stressed', emoji: '😤', label: 'Stressed', color: 'bg-red-100 hover:bg-red-200 border-red-300' },
  { value: 'tired', emoji: '😴', label: 'Tired', color: 'bg-gray-100 hover:bg-gray-200 border-gray-300' },
  { value: 'motivated', emoji: '💪', label: 'Motivated', color: 'bg-orange-100 hover:bg-orange-200 border-orange-300' },
];

const ENERGY_LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const MoodLogger = ({ isOpen, onClose, onMoodLogged }) => {
  const [selectedMood, setSelectedMood] = useState(null);
  const [energyLevel, setEnergyLevel] = useState(5);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [recentMoods, setRecentMoods] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadRecentMoods();
    }
  }, [isOpen]);

  const loadRecentMoods = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/mood/recent`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setRecentMoods(data.moods || []);
      }
    } catch (error) {
      console.error('Error loading recent moods:', error);
    }
  };

  const handleSubmit = async () => {
    if (!selectedMood) {
      toast.error('Please select a mood');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/mood/log`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            mood: selectedMood,
            energy_level: energyLevel,
            notes: notes.trim() || null
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success('✨ Mood Logged!', {
          description: data.message || 'Your mood has been recorded.'
        });
        
        // Reset form
        setSelectedMood(null);
        setEnergyLevel(5);
        setNotes('');
        
        if (onMoodLogged) onMoodLogged();
        onClose();
      } else {
        throw new Error('Failed to log mood');
      }
    } catch (error) {
      console.error('Error logging mood:', error);
      toast.error('Failed to log mood', {
        description: 'Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const getMoodOption = (value) => MOOD_OPTIONS.find(m => m.value === value);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-pink-500" />
            How are you feeling?
          </DialogTitle>
        </DialogHeader>

        {/* Mood Selection */}
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-3">Select your current mood:</p>
            <div className="grid grid-cols-4 gap-2">
              {MOOD_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedMood(option.value)}
                  className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                    selectedMood === option.value
                      ? `${option.color} border-2 border-gray-800 scale-105`
                      : `bg-gray-50 border-gray-200 hover:border-gray-300`
                  }`}
                >
                  <span className="text-2xl mb-1">{option.emoji}</span>
                  <span className="text-xs text-gray-700">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Energy Level */}
          <div>
            <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
              <Zap className="h-4 w-4 text-yellow-500" />
              Energy Level: <span className="font-semibold">{energyLevel}/10</span>
            </p>
            <div className="flex gap-1">
              {ENERGY_LEVELS.map((level) => (
                <button
                  key={level}
                  onClick={() => setEnergyLevel(level)}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                    energyLevel >= level
                      ? 'bg-gradient-to-t from-yellow-400 to-yellow-300 text-yellow-900'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <p className="text-sm text-gray-600 mb-2">Add a note (optional):</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What's on your mind?"
              className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
              rows={3}
            />
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={!selectedMood || loading}
            className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
          >
            {loading ? 'Logging...' : '✨ Log My Mood'}
          </Button>

          {/* Recent Moods */}
          {recentMoods.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                Recent moods
              </p>
              <div className="flex gap-2 flex-wrap">
                {recentMoods.slice(0, 5).map((mood, index) => {
                  const option = getMoodOption(mood.mood);
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-full text-xs"
                      title={new Date(mood.timestamp).toLocaleString()}
                    >
                      <span>{option?.emoji || '😐'}</span>
                      <span className="text-gray-600">{option?.label || mood.mood}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MoodLogger;
